// Bloque "Exigibilidad del grupo" — se muestra ARRIBA de la tabla de integrantes en
// Cartera.jsx cuando el crédito es grupal (`credito.integrantes.length > 0`).
//
// REGLA CRÍTICA (jul-2026): el número grande debe ser EXACTAMENTE el mismo que precarga
// el formulario de pago (openPay en Cartera.jsx). En un crédito grupal NO hay pago a
// nivel grupo — cada integrante se paga por separado con `sugeridoDePago({}, cuota)`.
// Por eso este bloque suma esa MISMA función por integrante y muestra el total. Si
// alguna vez la lógica de openPay cambia, este bloque debe cambiar con ella para no
// mostrar un número distinto.
//
// Subtítulo opcional (solo si hay atraso): "Incluye $X de cuota vencida y $Y de mora".
//   X = suma de la cuota pendiente de integrantes con dias_atraso > 0.
//   Y = suma de mora corriente + IVA mora corriente por integrante.
import { sugeridoDePago } from "@/lib/sugeridoPago";
import { mxn } from "@/lib/format";
import { AlertTriangle } from "lucide-react";

const r2 = (n) => Math.round(Number(n || 0) * 100) / 100;

const proximaCuotaDe = (integ) =>
  (integ?.tabla_amortizacion || []).find((c) => c.estatus !== "Pagada");

export function ExigibilidadGrupoBloque({ credito }) {
  if (!credito?.integrantes?.length) return null;

  let total = 0;
  let cuotaVencidaAcum = 0;
  let moraAcum = 0;
  let integrantesConAtraso = 0;

  for (const integ of credito.integrantes) {
    const cuota = proximaCuotaDe(integ);
    if (!cuota) continue;
    // MISMO llamado que openPay(integ) en Cartera.jsx: credito vacío + cuota del integrante.
    // Diferidos y GC del grupo NO se incluyen (viven a nivel crédito pero no precargan
    // en el form per-integrante — política vigente del motor).
    const sug = sugeridoDePago({}, cuota);
    total += sug.total;
    if ((cuota.dias_atraso || 0) > 0 || cuota.mora_congelada) {
      integrantesConAtraso += 1;
      cuotaVencidaAcum += sug.cuota + sug.cuotaComision;
    }
    moraAcum += sug.moraCorriente + sug.ivaMoraCorriente;
  }
  total = r2(total);
  cuotaVencidaAcum = r2(cuotaVencidaAcum);
  moraAcum = r2(moraAcum);

  const hayAtraso = integrantesConAtraso > 0 || moraAcum > 0.005;

  return (
    <div
      className={`mt-3 rounded-xl border p-4 shadow-sm ${
        hayAtraso
          ? "border-red-200 bg-gradient-to-br from-red-50 to-white"
          : "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white"
      }`}
      data-testid="exigibilidad-grupo"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Exigibilidad del grupo
          </p>
          <p className="mt-0.5 text-[11px] text-slate-400">
            Total a reunir para saldar el periodo del grupo (suma de la sugerencia por integrante).
          </p>
        </div>
        {hayAtraso && (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-100 px-2 py-0.5 text-[10.5px] font-semibold text-red-700"
            data-testid="exigibilidad-badge-atraso"
          >
            <AlertTriangle className="h-3 w-3" />
            {integrantesConAtraso} en atraso
          </span>
        )}
      </div>
      <p
        className={`mt-1 font-heading text-3xl font-bold tabular-nums sm:text-4xl ${
          hayAtraso ? "text-red-700" : "text-emerald-700"
        }`}
        data-testid="exigibilidad-total"
      >
        {mxn(total)}
      </p>
      {hayAtraso && (
        <p className="mt-1 text-xs text-slate-600" data-testid="exigibilidad-subtitulo">
          Incluye{" "}
          <span className="font-semibold tabular-nums" data-testid="exigibilidad-cuota-vencida">
            {mxn(cuotaVencidaAcum)}
          </span>{" "}
          de cuota vencida y{" "}
          <span className="font-semibold tabular-nums" data-testid="exigibilidad-mora">
            {mxn(moraAcum)}
          </span>{" "}
          de mora.
        </p>
      )}
    </div>
  );
}
